import { useState, useEffect, Fragment } from 'react';
// @mui
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  Stack,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// form
import { useFormContext } from 'react-hook-form';
// hooks
import useResponsive from '../../hooks/useResponsive';
// components
import { RHFTextField, RHFMultiCheckbox } from '../../components/hook-form';
import DIOText from '../../components/DIOText';
import Iconify from '../../components/Iconify';

// ----------------------------------------------------------------------

const REASON_OPTIONS = [
  '우리 회사가 찾는 포지션과 차이가 있어요',
  '크루가 제안한 금액이 부담스러워요',
  '크루의 근무 가능 시간이 너무 짧아요',
  '우리와 핏이 잘 맞지 않을 것 같아요',
  '다른 분으로 채용이 완료됐어요',
  '기타',
];

export default function RejectCrewDialog({ isOpen, closeDialog, isContract=false, submit, isSubmit=false }) {
  const smUp = useResponsive('up', 'sm');
  const [step, setStep] = useState(0);
  const [showEtc, setShowEtc] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const { getValues, watch, reset, resetField } = useFormContext();

  const resetAllState = async () => {
    setStep(0);
    setShowEtc(false);
    setCanNext(false);
    setCanSubmit(false);
  }

  const resetStateAndCloseDialog = async () => {
    closeDialog();
    resetAllState();
    reset();
    if (REASON_OPTIONS.indexOf("기타") == -1) {
      REASON_OPTIONS.push("기타")
    }
  }

  const watchValues = async (value, name, type) => {
    if(name === 'feedback') {
      setCanSubmit(!!value.feedback);
      return;
    }
    if(name !== 'reason' && name !== 'reasonEtc') return;
    if(type !== 'change') return;
    if(!showEtc && value.reason.indexOf("기타") !== -1) {
      REASON_OPTIONS.pop();
      setShowEtc(true);
      getValues().reason.pop();  // remove '기타'
    }
    setCanNext(value.reason.length || !!value.reasonEtc);
  }

  const uncheckEtc = async () => {
    if (showEtc) {
      setShowEtc(false);
      REASON_OPTIONS.push("기타");
      resetField('reasonEtc');
    }
  }

  const goNextForm = async () => {
    setStep(step+1);
  }

  const submitForm = async () => {
    const data = getValues();
    const submitData = {
      reason: [...data.reason, data.reasonEtc].filter(m => m).join('\n'),
      feedback: data.feedback,
    }
    await submit(submitData);
    resetAllState();
  }

  useEffect(() => {
    const subscription = watch((value, { name, type }) => watchValues(value, name, type ));
    return () => subscription.unsubscribe();
  }, [watch]);

  return (
    <Dialog open={isOpen} maxWidth="xs" fullWidth>
      {step === 0 && ( // [step 0]
        <Fragment>
          <DialogContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Iconify icon={"eva:alert-triangle-fill"} sx={{ width: 20, height: 20, color: 'error.main' }} />
              <Typography variant="subtitle2" sx={{ color: "error.main" }}>
                잠깐, 꼭 확인해주세요
              </Typography>
            </Stack>
            <Typography variant={smUp ? "h5" : "subtitle1"} gutterBottom>
              이 크루를 다시는 매칭받을 수 없어요.
            </Typography>
            <Typography variant={smUp ? "body1" : "body2" } sx={{ wordBreak: "keep-all" }}>
              그래도 다른 매칭을 받길 원하시는지 신중하게 결정해주세요.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Stack direction={{ xs: "column-reverse", sm: "row" }} sx={{ width: 1 }} spacing={1} justifyContent="flex-end">
            <Button
              onClick={resetStateAndCloseDialog}
              size="large"
              sx={{ px: 4 }}
            >
              돌아가기
            </Button>
            <Button
              variant="contained"
              onClick={goNextForm}
              size="large"
              sx={{ px: 4 }}
            >
              다른 크루 매칭받기
            </Button>
            </Stack>
          </DialogActions>
        </Fragment>
      )}
      {step === 2 && ( // [step 2]
        <Fragment>
          <DialogTitle sx={{ mb: 1 }}>
            거절하시는 자세한 이유를 알려주세요.
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3}>
              <DialogContentText>
                작성하신 내용은 다음 매칭 참고를 위해 <DIOText/>에만 전달됩니다.
              </DialogContentText>
              <RHFTextField
                label="거절 사유"
                name="feedback"
                multiline
                minRows={3}
                placeholder="예. 커머스 경력이 있는 분을 원합니다 / 와이어프레임 작업이 가능한 분을 원합니다"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <LoadingButton
              onClick={resetStateAndCloseDialog}
              loading={isSubmit}
              sx={{ px: { xs: 4, sm: 6 } }}
            >
              취소
            </LoadingButton>
            <LoadingButton
              variant="contained"
              onClick={submitForm}
              disabled={!canSubmit}
              loading={isSubmit}
              sx={{ px: 4 }}
            >
              제출하기
            </LoadingButton>
          </DialogActions>
        </Fragment>
      )}
      {step === 1 && ( // [step 1]
        <Fragment>
          <DialogTitle sx={{ mb: 1 }}>
            {isContract ? "계약" : "미팅"}하지 않는 이유를 선택해주세요.
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              크루에게 사유를 전달해드릴게요.
            </DialogContentText>
            <RHFMultiCheckbox name="reason" options={REASON_OPTIONS} sx={{ width: 1 }} />
            {showEtc && (
              <RHFTextField
                name="reasonEtc"
                placeholder="기타 사유를 입력해주세요"
                fullWidth
                size="small"
                autoFocus
                sx={{ 
                  background: (theme) => theme.palette.grey[200],
                  border: null,
                  borderRadius: 1,
                  my: 0.5,
                  "&.MuiTextField-root": {"&>.MuiInputBase-root": { pl: 0 } 
                }}}
                InputProps={{
                  startAdornment: <Checkbox checked={showEtc} onChange={uncheckEtc} />,
                  style: {fontSize: `0.875rem` }
                }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={resetStateAndCloseDialog}
              sx={{ px: { xs: 4, sm: 6 } }}
            >
              취소
            </Button>
            <Button
              variant="contained"
              onClick={goNextForm}
              disabled={!canNext}
              sx={{ px: { xs: 4, sm: 6 } }}
            >
              다음
            </Button>
          </DialogActions>
        </Fragment>
      )}
    </Dialog>
  )
};
